import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please log in");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <Logo size="lg" tagline />
      <div className="sms-tweed rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] w-full max-w-sm p-6 mt-8 sms-fade-up text-white">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={20} className="text-[#a9d79b]" />
          <h2 className="text-xl font-extrabold text-white">Set a new password</h2>
        </div>
        {!token ? (
          <p className="text-sm text-white/60 mt-2" data-testid="reset-no-token">This reset link is invalid. Please request a new one from the login page.</p>
        ) : (
          <form onSubmit={submit} className="mt-4">
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input data-testid="reset-password-input" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 pl-10 pr-4 py-3 outline-none border-2 border-[#5d8a52]" />
            </div>
            <div className="relative mt-3">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 pl-10 pr-4 py-3 outline-none border-2 border-[#5d8a52]" />
            </div>
            <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl mt-4 flex items-center justify-center gap-2 disabled:opacity-70">
              {busy && <Loader2 size={16} className="spin" />} Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
